import React from 'react';
import { Table, Spinner, Alert, Badge } from 'react-bootstrap';
import NoResult from './NoResult';
import EntityCard, { EntityCardDetail } from './EntityCard';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import { formatDateOnly } from '@core/utils/formatDateOnly';

/**
 * Role of a column in the mobile card layout (< md). Drives where the column's
 * value lands in `EntityCard`. Optional and backward compatible — when no column
 * declares a role, the card falls back to: first column = title, last = actions,
 * the rest = meta rows.
 */
export type DataTableColumnRole = 'title' | 'status' | 'actions' | 'description' | 'meta';

export interface DataTableColumn<T> {
  key: keyof T | string;
  label: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  className?: string;
  /** Slot this column maps to in the mobile card layout (default: 'meta'). */
  role?: DataTableColumnRole;
  /** Override the meta-row label used in the card (defaults to `label`). */
  cardLabel?: string;
  /** Override the cell renderer used in the card (defaults to `render`). */
  cardRender?: (value: any, row: T, index: number) => React.ReactNode;
  /** Omit this column from the mobile card entirely. */
  cardHidden?: boolean;
  /** Omit this column from the desktop table — e.g. a card-only description. */
  tableHidden?: boolean;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  showRowNumbers?: boolean;
  rowNumberOffset?: number;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  className?: string;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  responsive?: boolean;
  testId?: string;
  onRowClick?: (row: T, index: number) => void;
  rowTestId?: (row: T, index: number) => string;
  /** Accessible label for each mobile card, e.g. `(row) => `Church ${row.name}``. */
  cardAriaLabel?: (row: T, index: number) => string;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  showRowNumbers = true,
  rowNumberOffset = 0,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  className = '',
  striped = true,
  hover = true,
  bordered = false,
  responsive = true,
  testId = 'data-table',
  onRowClick,
  rowTestId,
  cardAriaLabel
}: DataTableProps<T>) {
  const tableClassName = [
    'mb-0',
    'align-middle',
    'ntcog-table',
    striped ? 'table-striped' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div
        className="text-center py-5"
        data-testid={`${testId}-loading`}
      >
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        className="mb-4"
        data-testid={`${testId}-error`}
      >
        {error}
      </Alert>
    );
  }

  if (data.length === 0) {
    return (
      <NoResult
        line_1={emptyMessage}
        data-testid={`${testId}-empty`}
      />
    );
  }

  // Resolve which column feeds each card slot. Role flags win; otherwise fall
  // back to first = title, last = actions, the rest = meta.
  const renderCardCell = (column: DataTableColumn<T>, row: T, index: number) => {
    const value = row[column.key as keyof T];
    if (column.cardRender) return column.cardRender(value, row, index);
    if (column.render) return column.render(value, row, index);
    return value as React.ReactNode;
  };

  const hasRoles = columns.some((c) => c.role);
  const titleCol = hasRoles ? columns.find((c) => c.role === 'title') ?? columns[0] : columns[0];
  const statusCol = hasRoles ? columns.find((c) => c.role === 'status') : undefined;
  const actionsCol = hasRoles
    ? columns.find((c) => c.role === 'actions')
    : columns[columns.length - 1];
  const descCol = hasRoles ? columns.find((c) => c.role === 'description') : undefined;
  const reservedCols = [titleCol, statusCol, actionsCol, descCol];
  const detailCols = columns.filter((c) => !reservedCols.includes(c) && !c.cardHidden);

  return (
    <div className={className}>
      {/* Mobile (< md): stacked cards */}
      <div className="ntcog-card-list d-md-none" data-testid={`${testId}-cards`}>
        {data.map((row, rowIndex) => {
          const details: EntityCardDetail[] = detailCols.map((c) => ({
            label: c.cardLabel ?? c.label,
            value: renderCardCell(c, row, rowIndex),
          }));
          return (
            <div key={rowIndex} className="ntcog-card-list__item">
              <EntityCard
                testId={rowTestId ? rowTestId(row, rowIndex) : `${testId}-card-${rowIndex}`}
                ariaLabel={cardAriaLabel?.(row, rowIndex)}
                title={titleCol ? renderCardCell(titleCol, row, rowIndex) : null}
                status={statusCol ? renderCardCell(statusCol, row, rowIndex) : undefined}
                actions={actionsCol ? renderCardCell(actionsCol, row, rowIndex) : undefined}
                description={descCol ? renderCardCell(descCol, row, rowIndex) : undefined}
                details={details}
                onActivate={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Desktop / tablet (>= md): table */}
      <div className="d-none d-md-block">
      <Table
        striped={striped}
        hover={hover}
        bordered={bordered}
        responsive={responsive}
        size="sm"
        className={tableClassName}
        data-testid={testId}
      >
        <thead className="table-light">
          <tr className="small text-muted">
            {showRowNumbers && (
              <th
                style={{ width: '72px' }}
                className="text-muted"
                data-testid={`${testId}-header-row-number`}
              >
                #
              </th>
            )}
            {columns.filter((c) => !c.tableHidden).map((column, index) => (
              <th
                key={index}
                style={{ width: column.width }}
                className={column.className}
                data-testid={`${testId}-header-${String(column.key)}`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={() => onRowClick?.(row, rowIndex)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              data-testid={rowTestId ? rowTestId(row, rowIndex) : `${testId}-row-${rowIndex}`}
            >
              {showRowNumbers && (
                <td
                  className="text-muted"
                  data-testid={`${testId}-cell-${rowIndex}-row-number`}
                >
                  {rowNumberOffset + rowIndex + 1}
                </td>
              )}
              {columns.filter((c) => !c.tableHidden).map((column, colIndex) => (
                <td
                  key={colIndex}
                  className={column.className}
                  data-testid={`${testId}-cell-${rowIndex}-${String(column.key)}`}
                >
                  {column.render
                    ? column.render(row[column.key as keyof T], row, rowIndex)
                    : row[column.key as keyof T]
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
    </div>
  );
}

// Helper function to create status badge
export const createStatusBadge = (status: string, variant?: string) => {
  const getVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return variant || 'info';
    }
  };

  return (
    <Badge {...subtleBadgeProps(getVariant(status))}>
      {status || 'Unknown'}
    </Badge>
  );
};

// Helper function to format calendar dates (YYYY-MM-DD from API).
export const formatDateOnlyDisplay = (
  dateString: string | undefined,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!dateString) return 'Not specified';
  return formatDateOnly(dateString, { year: 'numeric', month: 'short', day: 'numeric', ...options }, 'en-US')
    ?? dateString;
};

/** @deprecated Use formatDateOnlyDisplay for calendar dates or parseApiUtcDateTime for datetimes. */
export const formatDate = formatDateOnlyDisplay;

// Helper function to truncate text
export const truncateText = (text: string | undefined, maxLength: number = 50) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};
